
interface DataItem {
  en: string;
  ar: string;
}

interface MenuItem {
  title: DataItem;
  description: DataItem;
  icon: any;
  path: string;
  badge?: string;
}

interface MenuCategory {
  category: DataItem;
  items: MenuItem[];
}

interface StatItem {
  title: DataItem;
  value: string;
  change: string;
  trend: "up" | "down";
}

export const dashboardItems: MenuCategory[] = [
  {
    category: { en: "Event Management", ar: "إدارة الفعاليات" },
    items: [
      {
        title: { en: "Events Calendar", ar: "تقويم الفعاليات" },
        description: { en: "Add, edit, and delete events", ar: "إضافة، تعديل وحذف الفعاليات" },
        icon: "Calendar",
        path: "/admin/events"
      },
      {
        title: { en: "Exhibitions", ar: "المعارض" },
        description: { en: "Manage exhibition details and schedules", ar: "إدارة تفاصيل ومواعيد المعارض" },
        icon: "FileText",
        path: "/admin/exhibitions"
      },
      {
        title: { en: "Festivals", ar: "المهرجانات" },
        description: { en: "Manage festivals and celebrations", ar: "إدارة المهرجانات والاحتفالات" },
        icon: "PenTool",
        path: "/admin/festivals"
      },
      {
        title: { en: "Seasons", ar: "المواسم" },
        description: { en: "Manage seasonal events and activities", ar: "إدارة الفعاليات والأنشطة الموسمية" },
        icon: "Calendar",
        path: "/admin/seasons"
      },
    ]
  },
  {
    category: { en: "Partners & Users", ar: "الشركاء والمستخدمين" },
    items: [
      {
        title: { en: "Partners", ar: "الشركاء" },
        description: { en: "Manage participating entities and partners", ar: "إدارة الجهات المشاركة والشركاء" },
        icon: "Building",
        path: "/admin/partners"
      },
      {
        title: { en: "Users", ar: "المستخدمين" },
        description: { en: "Manage users and permissions", ar: "إدارة المستخدمين والصلاحيات" },
        icon: "Users",
        path: "/admin/users"
      },
      {
        title: { en: "Registrations", ar: "التسجيلات" },
        description: { en: "Manage event registrations", ar: "إدارة التسجيلات في الفعاليات" },
        icon: "UserPlus",
        path: "/admin/registrations"
      },
      {
        title: { en: "Registration Notifications", ar: "إشعارات التسجيل" },
        description: { en: "View new member registrations", ar: "عرض تسجيلات الأعضاء الجدد" },
        icon: "UserPlus",
        path: "/admin/registration-notifications",
        badge: "New"
      },
      {
        title: { en: "User Submissions", ar: "طلبات الإضافة" },
        description: { en: "Review and approve user submissions", ar: "مراجعة والموافقة على طلبات المستخدمين" },
        icon: "FileCheck",
        path: "/admin/submissions",
        badge: "New"
      },
    ]
  },
  {
    category: { en: "Communications", ar: "الاتصالات" },
    items: [
      {
        title: { en: "X Spaces", ar: "مساحات X" },
        description: { en: "Manage X (Twitter) Spaces links", ar: "إدارة روابط مساحات منصة X" },
        icon: "Twitter",
        path: "/admin/x-spaces",
        badge: "New"
      },
      {
        title: { en: "Social Media Groups", ar: "مجموعات التواصل" },
        description: { en: "Manage WhatsApp and Telegram groups", ar: "إدارة مجموعات الواتساب والتلجرام" },
        icon: "MessageSquare",
        path: "/admin/social-groups"
      },
      {
        title: { en: "News Ticker", ar: "شريط الأخبار" },
        description: { en: "Manage news ticker content", ar: "إدارة محتوى شريط الأخبار" },
        icon: "Newspaper",
        path: "/admin/news-ticker",
        badge: "New"
      },
      {
        title: { en: "Notifications", ar: "الإشعارات" },
        description: { en: "Manage system notifications", ar: "إدارة إشعارات النظام" },
        icon: "Bell",
        path: "/admin/notifications"
      },
      {
        title: { en: "Newsletter", ar: "النشرة الإخبارية" },
        description: { en: "Manage newsletter settings", ar: "إدارة إعدادات النشرة الإخبارية" },
        icon: "Mail",
        path: "/admin/newsletter"
      },
    ]
  },
  {
    category: { en: "Marketing & Revenue", ar: "التسويق والإيرادات" },
    items: [
      {
        title: { en: "Ads Management", ar: "إدارة الإعلانات" },
        description: { en: "Manage paid advertisements", ar: "إدارة الإعلانات المدفوعة" },
        icon: "Megaphone",
        path: "/admin/ads",
        badge: "New"
      },
      {
        title: { en: "Fixed Site Ads", ar: "إعلانات الموقع الثابتة" },
        description: { en: "Manage fixed ads on all pages", ar: "إدارة الإعلانات الثابتة بجميع الصفحات" },
        icon: "Image",
        path: "/admin/site-ads",
      },
      {
        title: { en: "Section Banner Ads", ar: "بنرات إعلانية بين الأقسام" },
        description: { en: "3 rental banners above each section + WhatsApp contact", ar: "3 بنرات للتأجير فوق كل قسم + تواصل واتساب" },
        icon: "Megaphone",
        path: "/admin/section-banner-ads",
        badge: "New"
      },
    ]
  },
  {
    category: { en: "System", ar: "النظام" },
    items: [
      {
        title: { en: "Events Import", ar: "استيراد الفعاليات" },
        description: { en: "Import events from external websites", ar: "استيراد الفعاليات من مواقع خارجية" },
        icon: "Download",
        path: "/admin/events-import",
        badge: "New"
      },
      {
        title: { en: "Categories Management", ar: "إدارة أيقونات الأقسام" },
        description: { en: "Manage navigation category icons", ar: "إدارة أيقونات تصفح الأقسام" },
        icon: "LayoutGrid",
        path: "/admin/categories",
        badge: "New"
      },
      {
        title: { en: "Section Visibility", ar: "إظهار/إخفاء الأقسام" },
        description: { en: "Control which sections appear on homepage", ar: "التحكم في الأقسام التي تظهر بالصفحة الرئيسية" },
        icon: "LayoutGrid",
        path: "/admin/section-visibility"
      },
      {
        title: { en: "Analytics", ar: "التحليلات" },
        description: { en: "View statistics and reports", ar: "عرض الإحصائيات والتقارير" },
        icon: "BarChart",
        path: "/admin/analytics"
      },
      {
        title: { en: "Settings", ar: "الإعدادات" },
        description: { en: "System settings and customization", ar: "إعدادات النظام والتخصيص" },
        icon: "Settings",
        path: "/admin/settings"
      },
    ]
  }
];

export type { DataItem, MenuItem, MenuCategory };
